import type { ReactNode } from 'react'
import { RotateCcw } from 'lucide-react'
import type { WavePlanCoreReceipts, WavePlanWeek } from '@/api/wavePlans'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatFullDate, formatUnits, weekLabel } from './model'

export interface CoreReceiptsChange {
  initial_week_id: string | null
  backup_week_id: string | null
}

const weekOption = (week: WavePlanWeek) =>
  [
    weekLabel(week.fiscal_week),
    formatFullDate(week.receipt_date ?? week.week_start_date),
    week.event_code,
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * Core plan on the AP's per-SKU quantities: the weeks that receive every SKU's
 * Final Initial Core Buy and Final Backup Buy Qty (the backup may come in with
 * the initial receipt). Until the planner chooses, the suggested weeks apply.
 */
export function CoreReceiptsCard({
  weeks,
  receipts,
  readOnly,
  pending,
  onChange,
}: {
  weeks: WavePlanWeek[]
  receipts: WavePlanCoreReceipts
  readOnly: boolean
  pending: boolean
  onChange: (change: CoreReceiptsChange) => void
}) {
  const usable = weeks.filter(week => week.is_wave_eligible && !week.removed)
  const isUsable = (id: string) => usable.some(week => week.id === id)
  const startOf = (id: string) =>
    weeks.find(week => week.id === id)?.week_start_date ?? ''
  const initialStart = receipts.initial_week_id
    ? startOf(receipts.initial_week_id)
    : ''
  const backupWeeks = usable.filter(
    week => week.week_start_date >= initialStart
  )
  // The stored choices, which a fallback to the suggestion does not erase.
  const chosenInitial =
    receipts.chosen_initial_week_id !== undefined
      ? receipts.chosen_initial_week_id
      : receipts.initial_source === 'PLANNER'
        ? receipts.initial_week_id
        : null
  const chosenBackup =
    receipts.chosen_backup_week_id !== undefined
      ? receipts.chosen_backup_week_id
      : receipts.backup_source === 'PLANNER'
        ? receipts.backup_week_id
        : null
  const labelOf = (id: string) => {
    const week = weeks.find(item => item.id === id)
    return week ? weekLabel(week.fiscal_week) : 'The chosen week'
  }
  const fallback = (chosen: string | null, effective: string | null) =>
    chosen && chosen !== effective
      ? effective
        ? `${labelOf(chosen)} can't receive a wave now; the suggested week is used until it can.`
        : `${labelOf(chosen)} can't receive a wave now.`
      : null
  // Nothing scheduled: no set-complete week to suggest from and no usable choice.
  const unscheduled = !receipts.initial_week_id
  const disabled = readOnly || pending

  return (
    <Card
      className="min-h-42 gap-3 p-4 shadow-none"
      data-testid="core-receipts"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Core receipts</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Every SKU&apos;s initial and backup buy from the assortment plan
            arrives in these weeks. The backup can come in with the initial
            receipt.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || (!chosenInitial && !chosenBackup)}
          onClick={() =>
            onChange({ initial_week_id: null, backup_week_id: null })
          }
        >
          <RotateCcw size={14} />
          Use suggested weeks
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <ReceiptField
          label="Initial receipt"
          units={receipts.initial_qty}
          suggested={receipts.initial_source === 'SUGGESTED'}
          note={
            fallback(chosenInitial, receipts.initial_week_id) ??
            (unscheduled
              ? 'No week can be suggested (set-complete date missing or no eligible week after the target). Choose the initial receipt week.'
              : null)
          }
        >
          <Select
            value={receipts.initial_week_id ?? undefined}
            disabled={disabled}
            onValueChange={id =>
              // A usable chosen backup before the new initial week goes back to
              // the suggestion; an unusable one is kept for when it can be used.
              onChange({
                initial_week_id: id,
                backup_week_id:
                  chosenBackup &&
                  (!isUsable(chosenBackup) ||
                    startOf(chosenBackup) >= startOf(id))
                    ? chosenBackup
                    : null,
              })
            }
          >
            <SelectTrigger
              aria-label="Initial receipt week"
              className="h-9 w-full text-xs"
            >
              <SelectValue placeholder="Choose a week" />
            </SelectTrigger>
            <SelectContent>
              {usable.map(week => (
                <SelectItem key={week.id} value={week.id}>
                  {weekOption(week)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ReceiptField>
        <ReceiptField
          label="Backup receipt"
          units={receipts.backup_qty}
          suggested={receipts.backup_source === 'SUGGESTED'}
          note={fallback(chosenBackup, receipts.backup_week_id)}
        >
          <Select
            value={receipts.backup_week_id ?? undefined}
            disabled={disabled || unscheduled}
            onValueChange={id =>
              onChange({ initial_week_id: chosenInitial, backup_week_id: id })
            }
          >
            <SelectTrigger
              aria-label="Backup receipt week"
              className="h-9 w-full text-xs"
            >
              <SelectValue placeholder="After the initial receipt" />
            </SelectTrigger>
            <SelectContent>
              {backupWeeks.map(week => (
                <SelectItem key={week.id} value={week.id}>
                  {week.id === receipts.initial_week_id
                    ? `Same week as initial · ${weekOption(week)}`
                    : weekOption(week)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ReceiptField>
      </div>
    </Card>
  )
}

function ReceiptField({
  label,
  units,
  suggested,
  note,
  children,
}: {
  label: string
  units: number
  suggested: boolean
  /** Why a chosen week is not the one used. */
  note: string | null
  children: ReactNode
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex min-h-6 items-center justify-between gap-2">
        <span className="text-xs font-medium">{label}</span>
        <span className="flex items-center gap-2 text-xs tabular-nums text-muted-foreground whitespace-nowrap">
          {formatUnits(units)} units
          {suggested && <Badge variant="secondary">Suggested</Badge>}
        </span>
      </div>
      {children}
      {note && (
        <p role="status" className="text-[11px] text-amber-800">
          {note}
        </p>
      )}
    </div>
  )
}
